import { Chip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, ReactNode } from 'react';

import ButtonsGroup, { IButtonGroupButton } from 'infrastructure/components/ButtonsGroup';

import {
  StyledBottomContainer,
  StyledCTAButton,
  StyledSideCTAButton,
  StyledContentContainer,
  StyledHorizontalCardContainer,
  StyledInnerRightCardContainer,
  StyledTitleContainer,
  StyledSideContainer,
  StyledCaptionContainer,
  StyledLabelContainer,
  StyledServicesContainer,
  StyledSubTitleContainer,
  StyledServicesDescriptionContainer,
  StyledTopCardContent,
} from './style';

export enum HorizontalCardType {
  Large = 'large',
  Small = 'small',
  NoCTA = 'noCTA',
}

interface IHorizontalCard {
  variant: HorizontalCardType;
  title: string;
  imageUrl: string;
  imageSx?: Record<string, unknown>;
  buttonList?: Array<IButtonGroupButton>;
  content?: ReactNode | string;
  ctaContent?: ReactNode | string;
  chipLabel?: string;
  handleCTA?: (employeeId?: string) => void;
  employeeId?: string;
  noBottomMargin?: boolean;
  boxShadow?: boolean;
  ctaButtonTop?: boolean;
  caption?: string;
  label?: string;
  services?: string;
  infoCardVariant?: boolean;
}
const HorizontalCard: FC<IHorizontalCard> = ({
  title,
  variant,
  imageUrl,
  imageSx,
  buttonList,
  chipLabel,
  handleCTA,
  employeeId,
  content,
  ctaContent,
  noBottomMargin,
  boxShadow,
  ctaButtonTop,
  caption,
  label,
  services,
  infoCardVariant,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down('md'));

  const imageSizeVariant =
    variant === HorizontalCardType.Large || variant === HorizontalCardType.NoCTA
      ? { width: '204px', height: '204px' }
      : { width: '84px', height: '84px' };

  const imageSizeMobile = { width: '40px', height: '40px' };

  const imageSize = isMobile ? imageSizeMobile : imageSizeVariant;
  return (
    <StyledHorizontalCardContainer
      sx={{
        ...(infoCardVariant && { flexDirection: 'column' }),
        ...(noBottomMargin && { marginBottom: 0 }),
        ...(boxShadow && {
          boxShadow:
            '0px 93px 26px 0px rgba(0, 0, 201, 0.00), 0px 59px 24px 0px rgba(201, 201, 201, 0.02), ' +
            '0px 33px 20px 0px rgba(201, 201, 201, 0.07), 0px 15px 15px 0px rgba(201, 201, 201, 0.12), ' +
            '0px 4px 8px 0px rgba(201, 201, 201, 0.14), 0px 0px 0px 0px rgba(201, 201, 201, 0.14)',
        }),
      }}>
      <StyledTopCardContent>
        {!isMobile && (
          <Box
            component={'img'}
            src={imageUrl}
            sx={{ ...imageSize, borderRadius: '8px', objectFit: 'cover', ...imageSx }}
          />
        )}
        <StyledInnerRightCardContainer
          sx={{ gap: variant === HorizontalCardType.Small ? theme.spacing(2) : theme.spacing(4) }}>
          {!isMobile ? (
            <StyledTitleContainer>
              <Typography variant={chipLabel ? 'h4' : 'h5'} color="text.primary">
                {title}
              </Typography>
              {chipLabel && (
                <Chip variant="filled" size="medium" color="secondary" label={chipLabel} />
              )}
              {handleCTA &&
                !isSmallerScreen &&
                variant !== HorizontalCardType.NoCTA &&
                ctaButtonTop &&
                [HorizontalCardType.Large].includes(variant) && (
                  <StyledCTAButton
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleCTA(employeeId)}>
                    {ctaContent ? ctaContent : 'Reserve Now'}
                  </StyledCTAButton>
                )}
            </StyledTitleContainer>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, mb: 4 }}>
              <Box
                component={'img'}
                src={imageUrl}
                sx={{ ...imageSize, borderRadius: '8px', ...imageSx }}
              />
              <StyledTitleContainer>
                <Typography variant={chipLabel ? 'h4' : 'h5'} color="text.primary">
                  {title}
                </Typography>
                {chipLabel && (
                  <Chip
                    variant="filled"
                    size={isMobile ? 'small' : 'medium'}
                    color="secondary"
                    label={chipLabel}
                    sx={isMobile ? { minWidth: 48, maxWidth: 80, px: 1 } : {}}
                  />
                )}
              </StyledTitleContainer>
            </Box>
          )}
          {infoCardVariant ? (
            <StyledServicesContainer>
              <StyledSubTitleContainer>
                {label && <StyledLabelContainer>{label}</StyledLabelContainer>}
                {caption && <StyledCaptionContainer>{caption}</StyledCaptionContainer>}
              </StyledSubTitleContainer>
              {services && (
                <StyledServicesDescriptionContainer>{services}</StyledServicesDescriptionContainer>
              )}
            </StyledServicesContainer>
          ) : (
            ''
          )}
          {[HorizontalCardType.Large, HorizontalCardType.Small, HorizontalCardType.NoCTA].includes(
            variant,
          ) &&
            (typeof content === 'string' ? (
              <StyledContentContainer>
                <div dangerouslySetInnerHTML={{ __html: content ?? '' }} />
              </StyledContentContainer>
            ) : (
              <StyledContentContainer>{content}</StyledContentContainer>
            ))}
        </StyledInnerRightCardContainer>
      </StyledTopCardContent>
      {handleCTA &&
        variant !== HorizontalCardType.NoCTA &&
        [HorizontalCardType.Large].includes(variant) && (
          <StyledBottomContainer>
            {buttonList && (
              <>
                <Box sx={{ display: 'flex', fontSize: 14, fontWeight: 700, paddingBottom: '8px' }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                    }}>
                    Pricing
                  </Typography>
                </Box>
                <ButtonsGroup
                  infoCardVariant={infoCardVariant}
                  buttonList={buttonList}
                  nonClickable={true}
                />
              </>
            )}
            {(!ctaButtonTop || isSmallerScreen) && (
              <StyledCTAButton
                variant="contained"
                color="primary"
                size="large"
                onClick={() => handleCTA(employeeId)}>
                {ctaContent ? ctaContent : 'Reserve Now'}
              </StyledCTAButton>
            )}
          </StyledBottomContainer>
        )}
      {handleCTA &&
        variant !== HorizontalCardType.NoCTA &&
        [HorizontalCardType.Small].includes(variant) && (
          <StyledSideContainer>
            <StyledSideCTAButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => handleCTA(employeeId)}>
              {ctaContent ? ctaContent : 'Reserve Now'}
            </StyledSideCTAButton>
          </StyledSideContainer>
        )}
    </StyledHorizontalCardContainer>
  );
};
export default HorizontalCard;
